<template>
  <transition name="modal">
    <div class="modal-mask account-popup-mask">
      <PopupPanel dark class="modal-container" aria-label="Set up your account">
        <PopupHeader class="modal-header">
          <slot name="header">
            <div class="brand">
              <img src="../assets/logo-n.svg" alt="Nvoko" />
              <span class="brand-title">Set up your account</span>
            </div>
            <div class="account">
              <span class="account-email">{{ userDetails.email }}</span>
              <button
                type="button"
                class="logout-button"
                aria-label="Log out"
                title="Log out"
                @click="logout"
              >
                <LogoutIcon :dark="true" />
              </button>
            </div>
          </slot>
        </PopupHeader>
        <div class="modal-body">
          <slot name="body">
            <div class="progress" v-if="!isMessageStep">
              <span class="progress-label"
                >Step {{ stepNumber }} of {{ stepTotal }}</span
              >
              <div
                class="progressbar"
                role="progressbar"
                :aria-valuenow="stepNumber"
                aria-valuemin="1"
                :aria-valuemax="stepTotal"
              >
                <div
                  class="line"
                  :style="{ width: progressPercent + '%' }"
                ></div>
              </div>
            </div>
            <div class="content">
              <div class="step-1" v-if="currentStep === 'role_selection'">
                <div class="step-intro">
                  <h3 class="title">How will you use Nvoko?</h3>
                  <p class="subtitle">
                    Choose the workspace that best matches your team structure
                    so we can tailor the onboarding experience.
                  </p>
                </div>
                <div class="radiobuttons dark multi-line">
                  <div class="radio-group">
                    <label class="radio">
                      <input
                        v-model="answer.role"
                        type="radio"
                        value="artist"
                      />
                      <span class="option-badge">Independent</span>
                      <span class="text"
                        >I'm an independent artist or producer</span
                      >
                      <span class="sub">
                        Manage my releases, invite collaborators, and sign my
                        own agreements.
                      </span>
                      <span class="checkmark"></span>
                    </label>
                  </div>
                  <div class="radio-group">
                    <label class="radio">
                      <input v-model="answer.role" type="radio" value="label" />
                      <span class="option-badge">Label / Management</span>
                      <span class="text"
                        >I'm a label or authorized management company</span
                      >
                      <span class="sub">
                        Set up our roster, invite my team, and handle catalogue
                        contracts directly.
                      </span>
                      <span class="checkmark"></span>
                    </label>
                  </div>
                </div>
                <div class="info-note">
                  <p>
                    Signed to a label that manages your releases?
                    <button type="button" @click="showLabelHandOffInfo">
                      See how label onboarding works.
                    </button>
                  </p>
                </div>
              </div>

              <div class="step-3" v-if="currentStep === 'artist_setup'">
                <div class="step-intro">
                  <h3 class="title">Build your profile</h3>
                  <p class="subtitle">
                    Add your creative details so we can prepare the right
                    agreements and invite collaborators on your behalf.
                  </p>
                </div>
                <AddArtistForm
                  v-model="answer"
                  not-signed
                  no-user-info
                  :require-songwriter-ipi="false"
                />
                <div class="section label-signed-section">
                  <h4>Label</h4>
                  <div class="option-cards">
                    <label class="option-card">
                      <input
                        type="checkbox"
                        v-model="answer.is_signed_to_label"
                      />
                      <span class="text">I am signed to a record label</span>
                      <span class="checkmark"></span>
                    </label>
                  </div>
                  <transition name="fade">
                    <div
                      v-if="answer.is_signed_to_label"
                      class="royalty-input-container"
                    >
                      <label class="royalty-label"
                        >What percentage of royalties does your label
                        receive?</label
                      >
                      <div class="royalty-input-wrapper">
                        <input
                          v-model.number="answer.label_royalty_percentage"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          class="text-input dark royalty-input"
                          placeholder="e.g. 7.5"
                        />
                        <span class="percentage-symbol">%</span>
                      </div>
                      <p class="royalty-helper">
                        This helps us calculate your share in collaboration
                        agreements.
                      </p>
                    </div>
                  </transition>
                </div>
              </div>

              <div class="step-4" v-if="currentStep === 'company_setup'">
                <div class="step-intro">
                  <h3 class="title">Set up your company</h3>
                  <p class="subtitle">
                    Confirm the people and artists you want to invite. We’ll set
                    up contracts based on this information.
                  </p>
                </div>
                <div class="section">
                  <h4>Company details</h4>
                  <div class="inputs">
                    <div class="form-group">
                      <label for="company-name"
                        >Label or company name (required)</label
                      ><input
                        id="company-name"
                        v-model="answer.name"
                        type="text"
                        class="text-input dark"
                        placeholder="Label or Company Name"
                        aria-label="Label or company name (required)"
                      />
                    </div>
                    <div class="form-group">
                      <label for="company-address"
                        >Business address (required)</label
                      >
                      <AddressInput
                        id="company-address"
                        v-model="answer.address"
                        title="Business Address*"
                      />
                    </div>
                  </div>
                  <div class="company-flags option-cards">
                    <label class="option-card">
                      <input
                        type="checkbox"
                        v-model="answer.recordingAgreement"
                      />
                      <span class="text"
                        >Our artists sign agreements with our company.</span
                      >
                      <span class="checkmark"></span>
                    </label>
                    <label class="option-card">
                      <input
                        type="checkbox"
                        v-model="answer.recordingAgreementThirdParty"
                      />
                      <span class="text"
                        >We also release music through third-party labels.</span
                      >
                      <span class="checkmark"></span>
                    </label>
                  </div>
                </div>
                <div class="section people">
                  <h4>Company representatives</h4>
                  <div
                    class="row"
                    v-for="({ firstName, lastName, me },
                    i) in answer.representatives"
                    :key="`rep-${i}`"
                  >
                    <template v-if="firstName && lastName">
                      <div class="initials">
                        {{ [firstName, lastName] | initials }}
                      </div>
                      <span>{{ firstName + " " + lastName }}</span>
                      <span class="you" v-if="me">You</span>
                      <button
                        type="button"
                        class="delete"
                        v-else
                        :aria-label="`Remove ${firstName} ${lastName}`"
                        @click="deleteRepresentative(i)"
                      >
                        &#x2715;
                      </button>
                    </template>
                  </div>
                  <button
                    type="button"
                    class="row add"
                    @click="openAddRepresentativeModal"
                  >
                    <span class="add-button" aria-hidden="true">+</span>
                    <span>Add another representative</span>
                  </button>
                </div>
                <div class="section people">
                  <h4>Artists and producers</h4>
                  <div
                    class="row"
                    v-for="(artist, i) in answer.artists"
                    :key="`artist-${i}`"
                  >
                    <template v-if="artist.talentName && i !== newArtistIndex">
                      <img
                        v-if="artist.avatar_url"
                        class="initials avatar"
                        :src="artist.avatar_url"
                        alt=""
                      />
                      <div v-else class="initials">
                        {{ [artist.talentName] | initials }}
                      </div>
                      <span class="row-text">
                        <span class="row-name">{{ artist.talentName }}</span>
                        <span class="row-meta" v-if="artistMeta(artist)">
                          {{ artistMeta(artist) }}
                        </span>
                      </span>
                      <span class="you" v-if="artist.me">You</span>
                      <template v-else>
                        <button
                          type="button"
                          class="edit"
                          :aria-label="`Edit ${artist.talentName}`"
                          @click="openEditArtistModal(i)"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          class="delete"
                          :aria-label="`Remove ${artist.talentName}`"
                          @click="deleteArtist(i)"
                        >
                          &#x2715;
                        </button>
                      </template>
                    </template>
                  </div>
                  <button
                    type="button"
                    class="row add"
                    @click="openAddArtistModal"
                  >
                    <span class="add-button" aria-hidden="true">+</span>
                    <span>Add artist / producer</span>
                  </button>
                </div>
              </div>

              <div class="step-7" v-if="currentStep === 'final_label_instead'">
                <div class="message-icon" aria-hidden="true">
                  <img src="../assets/logo-n.svg" alt="" />
                </div>
                <h3 class="title">Your label sets this up</h3>
                <p>
                  Thanks for reaching out. Since your label manages your
                  releases, please ask them to create the Nvoko account on your
                  behalf. We'll help them get everything ready for you.
                </p>
              </div>
            </div>
          </slot>
        </div>
        <PopupFooter class="modal-footer">
          <slot name="footer">
            <PopupActions class="buttons">
              <button
                v-if="showPrevious"
                class="primary outline dark"
                :disabled="activeSteps.length <= 1"
                @click="previous"
              >
                Back
              </button>
              <button
                v-if="showNext"
                :disabled="!stepValid"
                class="primary"
                @click="next"
              >
                Next
              </button>
              <button
                v-if="showFinish"
                :disabled="!stepValid || submitting"
                class="primary"
                @click="submit"
              >
                {{ submitting ? "Finishing..." : "Finish!" }}
              </button>
              <template v-if="isMessageStep">
                <button class="primary outline dark" @click="logout">
                  Log out
                </button>
                <button class="primary" @click="previous">
                  Back to options
                </button>
              </template>
            </PopupActions>
          </slot>
        </PopupFooter>
      </PopupPanel>
      <AddArtist
        v-if="addingArtist"
        v-model="answer.artists[artistModalIndex]"
        @close="closeAddArtistModal"
        :valid="validate(1)"
        :editing="editingArtist !== null"
        :require-songwriter-ipi="false"
        not-signed
      />
      <AddRepresentative
        v-if="addingRepresentative"
        v-model="answer.representatives[answer.representatives.length - 1]"
        @close="closeAddRepresentative"
        :valid="validate(2)"
      />
    </div>
  </transition>
</template>

<script>
import LogoutIcon from "@/assets/icons/Logout.vue";
import AddArtistForm from "@/components/AddArtistForm.vue";
import AddressInput from "@/components/AddressInput.vue";
import AddArtist from "@/components/modals/AddArtist.vue";
import AddRepresentative from "@/components/modals/AddRepresentative.vue";
import { normalizeEmail } from "@/utils/normalizeEmail";
import artistFields from "@/utils/artistFields";

import ONBOARDING from "@/api/mutations/ONBOARDING.gql";
import SYNC_CONTACTS from "@/api/mutations/SYNC_CONTACTS.gql";
import GET_MY_ARTISTS from "@/api/queries/GET_MY_ARTISTS.gql";

export default {
  name: "Onboarding",
  components: {
    LogoutIcon,
    AddArtistForm,
    AddArtist,
    AddRepresentative,
    AddressInput
  },
  data() {
    return {
      addingArtist: false,
      // Index + snapshot of the artist being edited, so Cancel can restore it
      editingArtist: null,
      addingRepresentative: false,
      submitting: false,
      answer: { role: null },
      activeSteps: ["role_selection"],
      stepSequence: ["role_selection"],
      savedAnswers: {},
      flow: null,
      validationSnapshots: {}
    };
  },
  watch: {
    "answer.role"(role) {
      if (this.currentStep !== "role_selection") return;
      if (!role) {
        this.flow = null;
        this.stepSequence = ["role_selection"];
        return;
      }

      this.flow = role;
      this.stepSequence = this.getStepSequenceForRole(role);
    },
    "answer.is_signed_to_label"(isSigned) {
      // Clear the royalty percentage when the checkbox is unchecked
      if (!isSigned) {
        this.answer.label_royalty_percentage = null;
      }
    }
  },
  computed: {
    userDetails() {
      // User is briefly null while logging out, before the redirect
      const user = this.$store.getters["account/getUser"] || {};
      return {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        me: true
      };
    },
    isNvoko() {
      return true || this.userDetails.email.includes("@nvoko.com");
    },
    currentStep() {
      return this.activeSteps[this.activeSteps.length - 1];
    },
    artistModalIndex() {
      return this.editingArtist
        ? this.editingArtist.index
        : this.answer.artists.length - 1;
    },
    // The blank entry behind an open "Add" modal isn't listed until saved
    newArtistIndex() {
      return this.addingArtist && !this.editingArtist
        ? this.answer.artists.length - 1
        : -1;
    },
    isMessageStep() {
      return ["final_label_instead"].includes(this.currentStep);
    },
    hasNextStep() {
      const index = this.stepSequence.indexOf(this.currentStep);
      return index > -1 && index < this.stepSequence.length - 1;
    },
    isFinalStep() {
      return ["artist_setup", "company_setup"].includes(this.currentStep);
    },
    showPrevious() {
      // The message step renders its own "Back to options" action
      return this.activeSteps.length > 1 && !this.isMessageStep;
    },
    showNext() {
      return !this.isMessageStep && this.hasNextStep;
    },
    showFinish() {
      return !this.isMessageStep && this.isFinalStep;
    },
    // Every flow is role selection plus one setup step, so count at least 2
    // even before a role is picked.
    stepTotal() {
      return Math.max(this.stepSequence.length, 2);
    },
    stepNumber() {
      return Math.max(this.stepSequence.indexOf(this.currentStep) + 1, 1);
    },
    progressPercent() {
      if (this.isMessageStep) return 100;
      return Math.min((this.stepNumber / this.stepTotal) * 100, 100);
    },
    allAnswers() {
      const answers = { ...this.savedAnswers };
      const currentStep = this.currentStep;
      if (currentStep && this.answer && Object.keys(this.answer).length > 0) {
        answers[currentStep] = this.cloneAnswer(this.answer);
      }
      return answers;
    },
    stepValid() {
      switch (this.currentStep) {
        case "role_selection": {
          const status = this.collectRoleStatus();
          this.logValidation("role_selection", status);
          return status.missing.length === 0;
        }
        case "artist_setup":
          return this.isArtistFormComplete;
        case "company_setup":
          return this.isCompanyFormComplete;
        default:
          return true;
      }
    },
    isArtistFormComplete() {
      const status = this.collectArtistStatus(this.answer);
      this.logValidation("artist_setup", status);
      return status.missing.length === 0;
    },
    isCompanyFormComplete() {
      const status = this.collectCompanyStatus(this.answer);
      this.logValidation("company_setup", status);
      return status.missing.length === 0;
    }
  },
  filters: {
    initials(names) {
      return names
        .map(el => {
          if (!el) return "";
          return el.charAt(0).toUpperCase();
        })
        .join("");
    }
  },
  methods: {
    capitalize(str = "") {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    deriveNameParts(nameSource = "", email = "") {
      const trimmedName = nameSource.trim();
      if (trimmedName) {
        const parts = trimmedName.split(/\s+/);
        const first = this.capitalize(parts[0]);
        const last = this.capitalize(parts.slice(1).join(" ")) || "Owner";
        return { first, last };
      }

      const local = email.split("@")[0] || "account";
      const cleaned = local.replace(/[_\-.]+/g, " ");
      const parts = cleaned.split(/\s+/).filter(Boolean);
      const first = this.capitalize(parts[0] || "Account");
      const last = this.capitalize(parts.slice(1).join(" ")) || "Owner";
      return { first, last };
    },
    normalizePrimaryArtist(artist = {}) {
      const normalized = { ...artist };
      const {
        firstName: userFirst,
        lastName: userLast,
        email: userEmail
      } = this.userDetails;
      const fallbacks = this.deriveNameParts(
        normalized.talentName || `${userFirst || ""} ${userLast || ""}`,
        userEmail || normalized.email || ""
      );

      normalized.firstName =
        normalized.firstName || userFirst || fallbacks.first || "Artist";
      normalized.lastName =
        normalized.lastName || userLast || fallbacks.last || "Owner";
      normalized.email = normalized.email || userEmail || "";
      normalized.email = normalizeEmail(normalized.email);

      return normalized;
    },
    normalizeRepresentative(rep = {}) {
      const normalized = { ...rep };
      if (!normalized.me) {
        return normalized;
      }

      const {
        firstName: userFirst,
        lastName: userLast,
        email: userEmail
      } = this.userDetails;
      const fallbacks = this.deriveNameParts(
        `${userFirst || ""} ${userLast || ""}`,
        userEmail || normalized.email || ""
      );

      normalized.firstName =
        normalized.firstName || userFirst || fallbacks.first || "Account";
      normalized.lastName =
        normalized.lastName || userLast || fallbacks.last || "Owner";
      normalized.email = normalized.email || userEmail || "";
      normalized.email = normalizeEmail(normalized.email);

      return normalized;
    },
    collectRoleStatus() {
      const hasRole = !!this.answer.role;
      return {
        missing: hasRole ? [] : ["role"],
        satisfied: hasRole ? ["role"] : []
      };
    },
    collectArtistStatus(artist) {
      const normalized = this.normalizePrimaryArtist(artist || {});
      const checks = {
        "artist.firstName": normalized.firstName,
        "artist.lastName": normalized.lastName,
        "artist.email": normalized.email,
        "artist.talentName": artist?.talentName,
        "artist.PROAffiliation": artist?.PROAffiliation,
        "artist.address": artist?.address,
        "artist.songwriterIPINo": this.optionalIPIValid(
          artist?.songwriterIPINo
        ),
        "artist.publisherIPINo": this.optionalIPIValid(artist?.publisherIPINo)
      };

      // If signed to label, require royalty percentage
      if (artist?.is_signed_to_label) {
        const percentage = artist?.label_royalty_percentage;
        checks["artist.label_royalty_percentage"] =
          percentage !== null &&
          percentage !== undefined &&
          percentage !== "" &&
          percentage >= 0 &&
          percentage <= 100;
      }

      const missing = Object.keys(checks).filter(key => !checks[key]);
      const satisfied = Object.keys(checks).filter(key => checks[key]);

      return { missing, satisfied };
    },
    collectCompanyStatus(company) {
      const missing = [];
      const satisfied = [];

      if (company?.name) satisfied.push("company.name");
      else missing.push("company.name");

      if (company?.address) satisfied.push("company.address");
      else missing.push("company.address");

      if (
        !Array.isArray(company?.representatives) ||
        !company.representatives.length
      ) {
        missing.push("representatives.none");
      } else {
        company.representatives.forEach((rep, index) => {
          const normalized = rep?.me
            ? this.normalizeRepresentative(rep)
            : rep || {};

          if (normalized.firstName)
            satisfied.push(`representatives[${index}].firstName`);
          else missing.push(`representatives[${index}].firstName`);

          if (normalized.lastName)
            satisfied.push(`representatives[${index}].lastName`);
          else missing.push(`representatives[${index}].lastName`);

          if (normalized.email)
            satisfied.push(`representatives[${index}].email`);
          else missing.push(`representatives[${index}].email`);
        });
      }

      if (!Array.isArray(company?.artists) || !company.artists.length) {
        missing.push("artists.none");
      } else {
        company.artists.forEach((artist, index) => {
          const normalized = artist?.me?.value
            ? this.normalizePrimaryArtist(artist)
            : artist || {};

          if (normalized.firstName)
            satisfied.push(`artists[${index}].firstName`);
          else missing.push(`artists[${index}].firstName`);

          if (normalized.lastName) satisfied.push(`artists[${index}].lastName`);
          else missing.push(`artists[${index}].lastName`);

          if (normalized.email) satisfied.push(`artists[${index}].email`);
          else missing.push(`artists[${index}].email`);

          if (artist?.talentName)
            satisfied.push(`artists[${index}].talentName`);
          else missing.push(`artists[${index}].talentName`);

          if (artist?.PROAffiliation)
            satisfied.push(`artists[${index}].PROAffiliation`);
          else missing.push(`artists[${index}].PROAffiliation`);

          if (artist?.address) satisfied.push(`artists[${index}].address`);
          else missing.push(`artists[${index}].address`);
        });
      }

      return { missing, satisfied };
    },
    logValidation(step, status) {
      const snapshot = status || { missing: [], satisfied: [] };
      const key = step;
      const fingerprint = JSON.stringify(snapshot);

      if (this.validationSnapshots[key] === fingerprint) return;
      this.$set(this.validationSnapshots, key, fingerprint);

      if (snapshot.missing.length) {
        console.log(`[Onboarding] ${step} missing:`, snapshot.missing);
      } else {
        console.log(`[Onboarding] ${step} complete`);
      }

      if (snapshot.satisfied && snapshot.satisfied.length) {
        console.log(`[Onboarding] ${step} satisfied:`, snapshot.satisfied);
      }
    },
    cloneAnswer(value) {
      return JSON.parse(JSON.stringify(value || {}));
    },
    persistCurrentAnswer(step) {
      if (!step) return;
      this.$set(this.savedAnswers, step, this.cloneAnswer(this.answer));
    },
    initializeAnswerForStep(step) {
      const saved = this.savedAnswers[step];
      if (saved) return this.cloneAnswer(saved);

      switch (step) {
        case "role_selection":
          return { role: null };
        case "artist_setup":
          return this.prepareArtistAnswer();
        case "company_setup":
          return this.prepareCompanyAnswer();
        case "final_label_instead":
          return {};
        default:
          return {};
      }
    },
    getStepSequenceForRole(role) {
      switch (role) {
        case "artist":
          return ["role_selection", "artist_setup"];
        case "label":
          return ["role_selection", "company_setup"];
        case "signed":
          return ["role_selection", "final_label_instead"];
        default:
          return ["role_selection"];
      }
    },
    prepareArtistAnswer() {
      const saved = this.savedAnswers.artist_setup;
      if (saved) return this.cloneAnswer(saved);

      const normalized = this.normalizePrimaryArtist({});

      return {
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        email: normalized.email,
        talentName: "",
        address: "",
        PROAffiliation: "",
        songwriterIPINo: "",
        publishingAdmin: "",
        publisherIPINo: "",
        recording_agreement: "no",
        recording_agreement_third_party: "no",
        me: { value: true },
        spotify_id: "",
        avatar_url: "",
        is_signed_to_label: false,
        label_royalty_percentage: null
      };
    },
    prepareCompanyAnswer() {
      const saved = this.savedAnswers.company_setup;
      if (saved) return this.cloneAnswer(saved);

      const representative = {
        ...this.normalizeRepresentative({ me: true }),
        me: true
      };

      return {
        name: "",
        address: "",
        recordingAgreement: true,
        recordingAgreementThirdParty: false,
        representatives: [representative],
        artists: []
      };
    },
    showLabelHandOffInfo() {
      if (this.currentStep !== "role_selection") return;
      this.persistCurrentAnswer("role_selection");
      this.flow = "signed";
      this.stepSequence = ["role_selection", "final_label_instead"];
      this.activeSteps = ["role_selection", "final_label_instead"];
      this.answer = this.initializeAnswerForStep("final_label_instead");
    },
    next() {
      if (!this.stepValid) return;

      const current = this.currentStep;
      this.persistCurrentAnswer(current);

      if (current === "role_selection") {
        const { role } = this.answer;
        this.flow = role || null;
        this.stepSequence = this.getStepSequenceForRole(role);
      }

      const index = this.stepSequence.indexOf(current);
      const nextStep =
        index > -1 && index < this.stepSequence.length - 1
          ? this.stepSequence[index + 1]
          : null;

      if (nextStep) {
        this.activeSteps.push(nextStep);
        this.answer = this.initializeAnswerForStep(nextStep);
      }
    },
    previous() {
      if (this.activeSteps.length <= 1) return;
      const current = this.currentStep;
      this.persistCurrentAnswer(current);
      this.activeSteps.pop();
      const previousStep = this.currentStep;
      this.answer = this.initializeAnswerForStep(previousStep);
      this.stepSequence = this.getStepSequenceForRole(this.flow || "artist");
      if (previousStep === "role_selection") {
        this.stepSequence = ["role_selection"];
        if (!this.answer.role) {
          this.flow = null;
        }
      }
    },
    validate(el) {
      if (!this.answer) return false;
      switch (el) {
        case 1:
          if (!Array.isArray(this.answer.artists)) return false;
          return this.answer.artists.every(artist => {
            const normalized = artist?.me?.value
              ? this.normalizePrimaryArtist(artist)
              : artist || {};

            return (
              normalized.firstName &&
              normalized.lastName &&
              artistFields.isValidEmail(normalized.email) &&
              artist.talentName &&
              artist.PROAffiliation &&
              artist.address &&
              this.optionalIPIValid(artist.songwriterIPINo) &&
              this.optionalIPIValid(artist.publisherIPINo)
            );
          });
        case 2:
          if (!Array.isArray(this.answer.representatives)) return false;
          return this.answer.representatives.every(rep => {
            const normalized = rep?.me
              ? this.normalizeRepresentative(rep)
              : rep || {};
            return (
              normalized.firstName && normalized.lastName && normalized.email
            );
          });
        default:
          return false;
      }
    },
    optionalIPIValid(val) {
      return !val || artistFields.isValidIPI(val);
    },
    validateIPI(val = "") {
      if (!val) return true;
      if (val.length !== 11) return false;

      const x = val.split("");
      const digits = x.splice(0, 9);

      let sum = 0;
      for (const [i, digit] of digits.entries()) {
        sum += Number(digit) * (10 - i);
      }

      sum = sum % 101;
      if (sum !== 0) sum = (101 - sum) % 100;
      sum = sum < 10 ? `0${sum}` : `${sum}`;

      if (sum !== x.join("")) return false;
      return true;
    },
    openAddArtistModal() {
      if (!Array.isArray(this.answer.artists)) {
        this.$set(this.answer, "artists", []);
      }
      this.answer.artists.push({});
      this.editingArtist = null;
      this.addingArtist = true;
    },
    openEditArtistModal(index) {
      this.editingArtist = {
        index,
        snapshot: JSON.parse(JSON.stringify(this.answer.artists[index]))
      };
      this.addingArtist = true;
    },
    closeAddArtistModal(val) {
      if (val === "cancelled") {
        if (this.editingArtist) {
          const { index, snapshot } = this.editingArtist;
          this.$set(this.answer.artists, index, snapshot);
        } else {
          this.answer.artists.pop();
        }
      }
      this.editingArtist = null;
      this.addingArtist = false;
    },
    artistMeta(artist) {
      const person = [artist.firstName, artist.lastName]
        .filter(Boolean)
        .join(" ");
      return [person, artist.PROAffiliation].filter(Boolean).join(" · ");
    },
    openAddRepresentativeModal() {
      if (!Array.isArray(this.answer.representatives)) {
        this.$set(this.answer, "representatives", []);
      }
      this.answer.representatives.push({});
      this.addingRepresentative = true;
    },
    closeAddRepresentative(val) {
      if (val === "cancelled") this.answer.representatives.pop();
      this.addingRepresentative = false;
    },
    deleteArtist(index) {
      if (!Array.isArray(this.answer.artists)) return;
      this.answer.artists.splice(index, 1);
    },
    deleteRepresentative(index) {
      if (!Array.isArray(this.answer.representatives)) return;
      this.answer.representatives.splice(index, 1);
    },
    formatRepresentatives(isMyArtist, representatives, company) {
      const user_id = this.$store.getters["account/getUserId"];

      return representatives.flatMap(rep => {
        if (!rep) return [];

        const { firstName, lastName, email, me } = rep.me
          ? this.normalizeRepresentative(rep)
          : rep;

        if (!(firstName && lastName && email)) {
          return [];
        }

        const isOrgOwner = !!me;

        const payload = {
          user_id_inviter: user_id,
          role: "representative",
          access: isOrgOwner ? "owner" : "full"
        };

        payload.user = {
          data: {
            first_name: firstName,
            last_name: lastName,
            display_name: `${firstName} ${lastName}`,
            email,
            status: "invited",
            onboarding_complete: true
          },
          on_conflict: {
            update_columns: ["email"],
            constraint: "users_email_key",
            where: { email: { _eq: email } }
          }
        };

        if (company) {
          payload.user.data.company_users = {
            data: {
              role: isOrgOwner ? "owner" : "representative",
              access: isOrgOwner ? "full" : "contributor",
              company: {
                data: { name: company.name, address: company.address },
                on_conflict: {
                  update_columns: ["name"],
                  constraint: "companies_name_key"
                }
              }
            },
            on_conflict: {
              update_columns: ["role", "access"],
              constraint: "company_users_company_id_user_id_key"
            }
          };
        }

        if (me) payload.user.data.id = user_id;

        return isMyArtist && me ? [] : [payload];
      });
    },
    formatArtists(
      artists,
      representatives,
      company,
      recording_agreement,
      recording_agreement_third_party
    ) {
      const user_id = this.$store.getters["account/getUserId"];

      return artists.map(artist => {
        const normalized = artist?.me?.value
          ? this.normalizePrimaryArtist(artist)
          : artist;

        const artistUser = {
          user_id_inviter: user_id,
          role: "artist",
          access: "owner"
        };

        if (normalized.me?.value) {
          artistUser.user = {
            data: {
              id: user_id,
              email: normalized.email,
              address: normalized.address
            },
            on_conflict: {
              constraint: "users_pkey",
              update_columns: ["address"]
            }
          };
        } else {
          artistUser.user = {
            data: {
              first_name: normalized.firstName,
              last_name: normalized.lastName,
              email: normalized.email,
              display_name: `${normalized.firstName} ${normalized.lastName}`,
              status: "invited",
              address: normalized.address,
              onboarding_complete: true
            },
            on_conflict: {
              update_columns: ["email"],
              constraint: "users_email_key",
              where: { email: { _eq: normalized.email } }
            }
          };
        }

        const reps = representatives.length
          ? this.formatRepresentatives(
              normalized.me?.value,
              representatives,
              company
            )
          : [];

        // Include label_royalty_percentage if artist is signed to a label
        const labelRoyaltyPercentage =
          normalized.is_signed_to_label &&
          normalized.label_royalty_percentage != null
            ? Number(normalized.label_royalty_percentage)
            : null;

        return {
          name: normalized.talentName,
          pro: normalized.PROAffiliation,
          ipi_songwriter: normalized.songwriterIPINo,
          ipi_publisher: normalized.publisherIPINo,
          publisher_name: normalized.publishingAdmin,
          address: normalized.address,
          spotify_id: normalized.spotify_id,
          avatar_url: normalized.avatar_url,
          label_royalty_percentage: labelRoyaltyPercentage,
          artist_users: {
            data: [artistUser, ...reps]
          },
          artist_preferences: { data: {} },
          company_artists: !company
            ? null
            : {
                data: {
                  recording_agreement,
                  recording_agreement_third_party,
                  company: {
                    data: { name: company.name, address: company.address },
                    on_conflict: {
                      update_columns: ["name"],
                      constraint: "companies_name_key"
                    }
                  }
                }
              }
        };
      });
    },
    async onboardUser(user_id, artists) {
      const {
        data: { update_users_by_pk, insert_artists }
      } = await this.$apollo.mutate({
        mutation: ONBOARDING,
        variables: { user_id, artists }
      });

      if (
        !update_users_by_pk.onboarding_complete ||
        !insert_artists.returning.length
      ) {
        throw new Error("Onboarding failed");
      }

      this.$store.commit("account/updateArtist", insert_artists.returning[0]);

      return insert_artists.returning.map(({ id }) => id);
    },
    async addConnections(artistId) {
      const user = this.$store.getters["account/getUser"];

      await this.$apollo.mutate({
        mutation: SYNC_CONTACTS,
        variables: {
          email: normalizeEmail(user.email),
          specific_artist_id: artistId || null,
          response: "accepted"
        }
      });
    },
    async refetchArtists() {
      const userId = this.$store.getters["account/getUserId"];
      const { data } = await this.$apollo.query({
        query: GET_MY_ARTISTS,
        variables: { userId },
        fetchPolicy: "network-only"
      });

      if (data && data.artists && data.artists.length > 0) {
        this.$store.commit("account/updateArtist", data.artists[0]);
      }
    },
    async submit() {
      if (!this.stepValid) return;

      this.persistCurrentAnswer(this.currentStep);

      try {
        this.submitting = true;

        const roleAnswer = this.allAnswers.role_selection || {};
        const role = roleAnswer.role;

        if (!role) {
          throw new Error("Please select how you will use Nvoko.");
        }

        let allArtists = [];
        let allRepresentatives = [];
        let companyProfile = null;
        let recording_agreement = false;
        let recording_agreement_third_party = false;

        if (role === "artist") {
          const artistData = this.normalizePrimaryArtist(
            this.callableAnswer("artist_setup")
          );
          if (!artistData) {
            throw new Error(
              "Missing artist information. Please complete the form."
            );
          }

          allArtists = [
            {
              ...artistData,
              me: { value: true }
            }
          ];
        } else if (role === "label") {
          const companyData = this.callableAnswer("company_setup");
          if (!companyData) {
            throw new Error(
              "Missing company information. Please complete the label onboarding form."
            );
          }

          allArtists = (companyData.artists || []).map(artist =>
            artist?.me?.value ? this.normalizePrimaryArtist(artist) : artist
          );
          allRepresentatives = (companyData.representatives || []).map(rep =>
            rep?.me ? this.normalizeRepresentative(rep) : rep
          );
          companyProfile = {
            name: companyData.name,
            address: companyData.address
          };
          recording_agreement = !!companyData.recordingAgreement;
          recording_agreement_third_party = !!companyData.recordingAgreementThirdParty;

          if (!allArtists.length) {
            throw new Error("Add at least one artist or producer to continue.");
          }
          if (!allRepresentatives.length) {
            throw new Error(
              "Add at least one company representative to continue onboarding."
            );
          }
        } else {
          throw new Error("This onboarding option redirects to your label.");
        }

        const artists = this.formatArtists(
          allArtists,
          allRepresentatives,
          companyProfile,
          recording_agreement,
          recording_agreement_third_party
        );

        const user_id = this.$store.getters["account/getUserId"];
        const artistIds = await this.onboardUser(user_id, artists);

        // Mark onboarding complete in the store immediately after the DB
        // mutation succeeds. This prevents a double-onboarding bug where
        // a failure in addConnections would leave the store stale while
        // the DB already has onboarding_complete=true + artist created.
        this.$store.commit("account/onboardingCompleted");

        // Sync collaborators (merge placeholders, accept contacts).
        // Non-blocking: don't let sync failure prevent the user from proceeding.
        try {
          await this.addConnections(artistIds[0]);
        } catch (syncError) {
          console.error("addConnections failed during onboarding:", syncError);
        }

        await this.refetchArtists();

        // If user came from an invite, redirect to Catalog with fromInvite
        // so they land on the pending contract review
        const fromInvite = localStorage.getItem("nvoko_from_invite");
        if (fromInvite) {
          localStorage.removeItem("nvoko_from_invite");
          window.location.href = "/catalog?fromInvite=true";
        } else {
          window.location.href = "/catalog";
        }
      } catch (e) {
        console.log(e.message);
        this.$errorService.handleApolloError(e, "onboarding");
      } finally {
        this.submitting = false;
      }
    },
    callableAnswer(step) {
      return this.allAnswers[step]
        ? this.cloneAnswer(this.allAnswers[step])
        : null;
    },
    logout() {
      this.$store.commit("account/logout");
      window.location.href = "/";
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_mixins.scss";

$ink: rgba(255, 255, 255, 0.92);
$muted: rgba(255, 255, 255, 0.66);
$line: rgba(255, 255, 255, 0.1);
$field: rgba(255, 255, 255, 0.05);
$column: 560px;

.account-popup-mask {
  @include auth-backdrop;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-container {
  --popup-max-width: 760px;
  --popup-surface: transparent;
  --popup-header-surface: transparent;
  --popup-line: #{$line};
  --popup-header-line: #{rgba($accent, 0.45)};
  --popup-muted: #{$muted};
  width: 100%;
  height: min(880px, calc(100dvh - 32px));
  margin: 0;
  position: relative;
  @include auth-glass-card;
}

// Header: brand on the left, signed-in account on the right
.modal-header {
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 16px;
  padding: 14px 20px 14px 24px !important;

  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    img {
      width: 28px;
      height: 28px;
    }

    .brand-title {
      color: $ink;
      font-size: 16px;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .account {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 4px 4px 4px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid $line;

    .account-email {
      color: $muted;
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .logout-button {
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      width: 36px;
      height: 36px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;
      transition: background-color 160ms ease;

      ::v-deep svg {
        width: 16px;
        height: 16px;
      }

      &:hover,
      &:focus-visible {
        background: rgba($accent, 0.16);
      }

      &:focus-visible {
        outline: 2px solid $accent;
        outline-offset: 1px;
      }
    }
  }
}

.modal-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 32px 36px !important;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.18);
    border-radius: 8px;
  }

  .progress {
    width: min(#{$column}, 100%);
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 32px;

    .progress-label {
      flex: 0 0 auto;
      color: $muted;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.02em;
    }

    .progressbar {
      flex: 1 1 auto;
      height: 6px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 999px;
      overflow: hidden;

      .line {
        height: 100%;
        background: linear-gradient(90deg, darken($accent, 8%), $accent);
        border-radius: 999px;
        box-shadow: 0 0 12px rgba($accent, 0.5);
        transition: width 300ms ease;
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .step-1,
  .step-3,
  .step-4,
  .step-7 {
    width: min(#{$column}, 100%);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    color: $ink;
  }

  .step-intro {
    text-align: center;
    margin-bottom: 28px;
  }

  .title {
    color: $white;
    margin: 0 0 10px;
    font-size: clamp(1.6rem, 3.4vw, 2rem);
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  .subtitle {
    max-width: 30rem;
    margin: 0 auto;
    color: $muted;
    font-size: 15px;
    line-height: 1.55;
  }

  // Role cards
  .radiobuttons.multi-line {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 24px;

    .radio-group {
      width: 100%;
      max-width: none;
      margin: 0;
      background: transparent;
      border-radius: 18px;
    }

    .radio {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      height: auto;
      min-height: 0;
      padding: 22px 64px 22px 24px;
      text-align: left;
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.035);
      border: 1px solid $line;
      cursor: pointer;
      transition: border-color 180ms ease, background-color 180ms ease;

      &:hover {
        border-color: rgba($accent, 0.45);
        background: rgba($accent, 0.05);
      }

      // Keyboard focus only, so mouse clicks don't show a ring
      &:has(input:focus-visible) {
        outline: 2px solid $accent;
        outline-offset: 2px;
      }

      // Visually hidden but still keyboard focusable
      input[type="radio"] {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
        visibility: visible;
      }

      .option-badge,
      .text,
      .sub {
        position: relative;
        z-index: 2;
      }

      .option-badge {
        display: inline-flex;
        align-items: center;
        padding: 4px 10px;
        margin-bottom: 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: $accent;
        background: rgba($accent, 0.12);
        border: 1px solid rgba($accent, 0.35);
      }

      .text {
        margin: 0;
        color: $white;
        font-size: 17px;
        font-weight: 600;
        line-height: 1.35;
      }

      .sub {
        margin: 6px 0 0;
        color: $muted;
        font-size: 14px;
        line-height: 1.5;
      }

      // Card highlight + radio indicator (top right)
      .checkmark {
        display: block;
        position: absolute;
        inset: -1px;
        width: auto;
        height: auto;
        z-index: 1;
        border-radius: 18px;
        border: 1px solid transparent;
        background: transparent;
        pointer-events: none;
        transition: background-color 180ms ease, border-color 180ms ease,
          box-shadow 180ms ease;

        &::after {
          content: "";
          display: block;
          position: absolute;
          top: 22px;
          right: 22px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: transparent;
          transition: border-color 180ms ease, box-shadow 180ms ease;
        }
      }

      input:checked ~ .checkmark {
        display: block;
        width: auto;
        height: auto;
        border-radius: 18px;
        border-color: $accent;
        background: linear-gradient(
          135deg,
          rgba($accent, 0.16),
          rgba($accent, 0.04)
        );
        box-shadow: 0 16px 40px rgba($accent, 0.18);

        &::after {
          display: block;
          border-color: $accent;
          box-shadow: inset 0 0 0 4px #1e1b1d, inset 0 0 0 11px $accent;
        }
      }

      input:checked ~ .option-badge {
        color: $white;
        background: $accent;
        border-color: $accent;
      }
    }
  }

  .info-note {
    text-align: center;
    color: $muted;
    font-size: 14px;
    line-height: 1.5;

    button {
      color: $accent;
      background: none;
      border: none;
      padding: 0;
      font: inherit;
      font-weight: 500;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        text-decoration: underline;
      }
    }
  }

  // Grouped form sections (artist / company steps)
  .section {
    width: 100%;
    padding: 20px 20px 4px;
    margin-bottom: 16px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid $line;

    h4 {
      margin: 0 0 16px;
      color: $accent;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      text-align: left;
    }

    .inputs {
      width: 100%;
      min-width: 0;
      max-width: none;
      margin-bottom: 0;
    }

    .form-group {
      margin-bottom: 16px;
    }
  }

  // Company step labels (AddArtistForm styles its own fields)
  .form-group > label {
    display: block;
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 13px;
    font-weight: 500;
    text-align: left;
  }

  // Fields owned by this page, including the company step's AddressInput
  .step-4 ::v-deep,
  .label-signed-section ::v-deep {
    input.text-input,
    input.text-input.dark,
    select.text-input {
      width: 100%;
      min-height: 46px;
      padding: 11px 16px;
      color: $white;
      font-size: 15px;
      font-weight: 400;
      background-color: $field;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 12px;
      transition: border-color 160ms ease, box-shadow 160ms ease,
        background-color 160ms ease;

      &::placeholder {
        color: rgba(255, 255, 255, 0.38);
      }

      &:hover {
        border-color: rgba(255, 255, 255, 0.22);
      }

      &:focus {
        outline: none;
        border-color: $accent;
        background-color: rgba($accent, 0.06);
        box-shadow: 0 0 0 4px rgba($accent, 0.16);
      }
    }

    select.text-input {
      padding-right: 40px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none' viewBox='0 0 12 8'%3E%3Cpath stroke='%23ffffff' stroke-opacity='.6' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M1.5 1.75 6 6.25l4.5-4.5'/%3E%3C/svg%3E");

      option {
        color: $white;
        background: #1e1b1d;
      }
    }
  }

  // Checkbox cards
  .option-cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;

    .option-card {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 14px 16px;
      border-radius: 12px;
      border: 1px solid $line;
      background: rgba(255, 255, 255, 0.03);
      color: $ink;
      font-size: 14px;
      cursor: pointer;
      transition: border-color 160ms ease, background-color 160ms ease;

      &:hover {
        border-color: rgba($accent, 0.45);
      }

      // Keyboard focus only, so mouse clicks don't show a ring
      &:has(input:focus-visible) {
        outline: 2px solid $accent;
        outline-offset: 2px;
      }

      input {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
      }

      .text {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 12px;
        color: $ink;
        font-weight: 400;
        line-height: 1.4;

        // Checkbox indicator
        &::before {
          content: "";
          flex: 0 0 20px;
          width: 20px;
          height: 20px;
          border-radius: 6px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: transparent no-repeat center / 12px 12px;
          transition: background-color 160ms ease, border-color 160ms ease;
        }
      }

      input:checked ~ .text::before {
        border-color: $accent;
        background-color: $accent;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 6.2 5 8.5l4.5-5' fill='none' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      }

      .checkmark {
        position: absolute;
        inset: -1px;
        border-radius: 12px;
        border: 1px solid transparent;
        pointer-events: none;
        transition: background-color 160ms ease, border-color 160ms ease;
      }

      input:checked ~ .checkmark {
        border-color: rgba($accent, 0.7);
        background: rgba($accent, 0.08);
      }
    }
  }

  .label-signed-section {
    .option-cards {
      margin-bottom: 16px;
    }

    .royalty-input-container {
      margin-bottom: 16px;
      padding: 16px;
      border-radius: 12px;
      background: rgba(0, 0, 0, 0.2);
      border: 1px solid $line;

      .royalty-label {
        display: block;
        margin-bottom: 10px;
        color: $ink;
        font-size: 14px;
        font-weight: 500;
      }

      .royalty-input-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
        max-width: 180px;

        .royalty-input {
          flex: 1;
          font-size: 16px;
          font-weight: 500;

          &::-webkit-outer-spin-button,
          &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          &[type="number"] {
            -moz-appearance: textfield;
          }
        }

        .percentage-symbol {
          color: $muted;
          font-size: 18px;
          font-weight: 600;
        }
      }

      .royalty-helper {
        margin-top: 10px;
        color: $muted;
        font-size: 13px;
        line-height: 1.45;
      }
    }
  }

  // People lists (company step)
  .people {
    padding-bottom: 12px;

    .row {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      min-height: 52px;
      padding: 8px 8px 8px 10px;
      margin-bottom: 8px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.04);
      color: $ink;
      font-size: 15px;

      &:empty {
        display: none;
      }

      .initials {
        display: grid;
        place-items: center;
        flex: 0 0 36px;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: linear-gradient(135deg, $accent, darken($accent, 18%));
        color: $white;
        font-size: 13px;
        font-weight: 600;
      }

      .avatar {
        object-fit: cover;
      }

      .row-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        line-height: 1.3;
      }

      .row-name,
      .row-meta {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .row-meta {
        color: $muted;
        font-size: 13px;
      }

      .edit {
        margin-left: auto;
        min-height: 36px;
        padding: 0 12px;
        border: 1px solid $line;
        border-radius: 999px;
        background: transparent;
        color: $ink;
        font: inherit;
        font-size: 13px;
        cursor: pointer;

        &:hover,
        &:focus-visible {
          background: rgba(255, 255, 255, 0.08);
        }

        + .delete {
          margin-left: 0;
        }
      }

      .you {
        margin-left: auto;
        padding: 2px 10px;
        border-radius: 999px;
        font-size: 12px;
        color: $muted;
        border: 1px solid $line;
      }

      .delete {
        display: grid;
        place-items: center;
        margin-left: auto;
        width: 36px;
        height: 36px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: $muted;
        cursor: pointer;

        &:hover,
        &:focus-visible {
          color: $white;
          background: rgba($accent, 0.2);
        }
      }
    }

    .row.add {
      border: 1px dashed rgba($accent, 0.45);
      background: transparent;
      color: $accent;
      font: inherit;
      font-size: 15px;
      text-align: left;
      cursor: pointer;
      transition: background-color 160ms ease, border-color 160ms ease;

      .add-button {
        display: grid;
        place-items: center;
        flex: 0 0 36px;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: rgba($accent, 0.14);
        color: $accent;
        font-size: 20px;
        line-height: 1;
      }

      &:hover,
      &:focus-visible {
        background: rgba($accent, 0.08);
        border-color: $accent;
      }

      &:focus-visible {
        outline: 2px solid $accent;
        outline-offset: 2px;
      }
    }
  }

  // Label hand-off message
  .step-7 {
    align-items: center;
    text-align: center;
    padding-top: 24px;

    .message-icon {
      display: grid;
      place-items: center;
      width: 80px;
      height: 80px;
      margin-bottom: 24px;
      border-radius: 22px;
      background: linear-gradient(
        145deg,
        rgba(255, 255, 255, 0.07),
        rgba(255, 255, 255, 0.02)
      );
      border: 1px solid $line;
      box-shadow: 0 0 40px rgba($accent, 0.25);

      img {
        width: 46px;
      }
    }

    p {
      max-width: 28rem;
      margin: 0;
      color: $muted;
      font-size: 16px;
      line-height: 1.6;
    }
  }
}

// Footer: Back on the left, primary action on the right
.modal-footer {
  padding: 14px 24px !important;

  .buttons {
    justify-content: space-between !important;
  }

  button {
    min-width: 120px;
  }

  button.primary:not(.outline) {
    background: linear-gradient(135deg, $accent, darken($accent, 15%));
    box-shadow: 0 8px 24px rgba($accent, 0.3);
    font-weight: 600;

    &:disabled {
      opacity: 0.45;
      box-shadow: none;
      cursor: not-allowed;
    }
  }

  // Keep the primary action on the right even when Back is hidden
  button.primary:not(.outline):only-child {
    margin-left: auto !important;
  }
}

@media (max-width: 600px) {
  .modal-container {
    height: calc(100dvh - 32px);
  }

  .modal-header {
    padding: 10px 12px 10px 16px !important;

    .brand .brand-title {
      display: none;
    }
  }

  .modal-body {
    padding: 20px 16px 28px !important;

    .radiobuttons.multi-line .radio {
      padding: 18px 56px 18px 18px;
    }

    .section {
      padding: 16px 14px 2px;
    }
  }

  .modal-footer {
    padding: 12px 16px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .modal-body * {
    transition: none !important;
  }
}

/* Fade transition for label royalty input */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
